import axios from '@/_utils/axios';
import { ScheduleRow } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/ReviewScheduleListAccordion';
import { LaunchType } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchDetailsRow';
import { getLaunchValidationExpansionState, getOccurrenceIso } from '@/components/Focus/utils/newScheduleAvails';
import { createLaunch } from '@/components/Focus/utils/incrementalLaunchWorkspace';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { AppThunk } from '../store';
import { IncrementalLaunchTimingValue } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchTimingSection/IncrementalLaunchTimingSection';
import {
  IncrementalLaunchRightsDraftByTab,
  IncrementalLaunchSelectedVersions
} from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchWorkspace';
import { rebaseFollowingLaunches } from '@/components/Focus/utils/incrementalLaunchList';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';

type ScheduleGroup = ScheduleRow[];

export type Launch = {
  id: string;
  type: LaunchType;
  incrementalLaunchTimingValue: IncrementalLaunchTimingValue;
  incrementalLaunchRightsDraftByTab: IncrementalLaunchRightsDraftByTab;
  incrementalLaunchVersions: IncrementalLaunchSelectedVersions;
};

type FocusRightCheckObject = {
  element_subtype: string;
  language: string;
};

export type FocusRightsPossibleResponse = {
  success: boolean;
  data: {
    territories: {
      [key: string]: {
        audio: FocusRightCheckObject[];
        timedtext: FocusRightCheckObject[];
        video: FocusRightCheckObject[];
      };
    };
  };
};

export type InvalidRight = {
  reason: string;
  title_id: number;
  territory: string;
  language: string;
  element_type: 'audio' | 'timedtext' | 'video';
  element_subtype: string;
};

type FocusRightsValidateResponse = {
  success: boolean;
  data: {
    is_valid: boolean;
    invalid_rights: InvalidRight[];
  };
};

export type RightsValidationResult = InvalidRight[] | 'valid';

export type VersionLaunchValidationState = {
  base: RightsValidationResult | null;
  launchesById: Record<string, RightsValidationResult>;
};

export type RightsValidateByVersionId = Record<number, VersionLaunchValidationState>;

type ValidationWindow = {
  term_start__gte: string;
  term_end__lte: string;
};

type FocusNewScheduleState = {
  view: 'search' | 'details' | 'review';
  selectedLicense: string;
  selectedPlatforms: string[];
  startDate: string;
  endDate: string;
  frequency: string;
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  hasReviewChanges: boolean;
  rightsTask: { status: 'none' | 'pending' | 'completed'; id: number | null };
  rightsTaskBySchedulingId: Record<number, { status: 'none' | 'pending' | 'completed'; id: number | null }>;
  rightsPossible: FocusRightsPossibleResponse['data'] | null;
  rightsValidate: InvalidRight[] | null;
  scheduleGroups: ScheduleGroup[];
  scheduleContextKey: string | null;
  rightsPossibleById: Record<number, FocusRightsPossibleResponse['data']>;
  rightsValidateById: Record<number, InvalidRight[] | 'valid'>;
  rightsPossibleBySchedulingId: Record<number, FocusRightsPossibleResponse['data']>;
  rightsValidateBySchedulingId: Record<number, InvalidRight[] | 'valid'>;
  rightsValidateByVersionId: RightsValidateByVersionId;
  validationWindowByVersionId: Record<number, ValidationWindow>;
  launchList: Launch[];
};

type LaunchValidationBatch = {
  versionIds: number[];
  term_start__gte: string;
  term_end__lte: string;
  territories: string[];
  audio: FocusRightCheckObject[];
  timedtext: FocusRightCheckObject[];
};

const initialState: FocusNewScheduleState = {
  view: 'search',
  selectedLicense: 'svod',
  selectedPlatforms: [],
  startDate: '',
  endDate: '',
  frequency: 'weekly',
  selectedTerritories: [],
  selectedDubs: [],
  selectedSubs: [],
  selectedVersions: [],
  hasReviewChanges: false,
  rightsTask: { status: 'none', id: null },
  rightsPossible: null,
  rightsValidate: null,
  scheduleGroups: [],
  scheduleContextKey: null,
  rightsPossibleById: {},
  rightsValidateById: {},
  rightsPossibleBySchedulingId: {},
  rightsValidateBySchedulingId: {},
  rightsValidateByVersionId: {},
  validationWindowByVersionId: {},
  rightsTaskBySchedulingId: {},
  launchList: []
};

let globalAbortController = new AbortController();

const ensureVersionLaunchValidationState = (state: FocusNewScheduleState, versionId: number) => {
  if (!state.rightsValidateByVersionId[versionId]) {
    state.rightsValidateByVersionId[versionId] = {
      base: null,
      launchesById: {}
    };
  }
};

const getInvalidRightsByTitleId = (invalidRights: InvalidRight[] = []): Record<number, InvalidRight[]> =>
  invalidRights.reduce<Record<number, InvalidRight[]>>((acc, right) => {
    if (!acc[right.title_id]) {
      acc[right.title_id] = [];
    }

    acc[right.title_id].push(right);
    return acc;
  }, {});

const setBaseValidationForVersion = ({
  state,
  versionId,
  validationResult,
  termStart,
  termEnd
}: {
  state: FocusNewScheduleState;
  versionId: number;
  validationResult: RightsValidationResult;
  termStart: string;
  termEnd: string;
}) => {
  ensureVersionLaunchValidationState(state, versionId);

  state.rightsValidateByVersionId[versionId].base = validationResult;
  state.validationWindowByVersionId[versionId] = {
    term_start__gte: termStart,
    term_end__lte: termEnd
  };
};

const setLaunchValidationForVersions = ({
  state,
  versionIds,
  launchId,
  isValid,
  invalidRights
}: {
  state: FocusNewScheduleState;
  versionIds: number[];
  launchId: string;
  isValid: boolean;
  invalidRights: InvalidRight[];
}) => {
  const invalidRightsByTitleId = getInvalidRightsByTitleId(invalidRights);

  versionIds.forEach((versionId) => {
    ensureVersionLaunchValidationState(state, versionId);

    state.rightsValidateByVersionId[versionId].launchesById[launchId] = isValid ? 'valid' : (invalidRightsByTitleId[versionId] ?? []);
  });
};

const createLaunchValidationBatchKey = ({
  term_start__gte,
  term_end__lte,
  territories,
  audio,
  timedtext
}: Omit<LaunchValidationBatch, 'versionIds'>) =>
  JSON.stringify({
    term_start__gte,
    term_end__lte,
    territories: [...territories].map((territory) => territory.toLowerCase()).sort(),
    audio: [...audio]
      .map((item) => ({
        language: item.language,
        element_subtype: item.element_subtype
      }))
      .sort((a, b) => a.language.localeCompare(b.language) || a.element_subtype.localeCompare(b.element_subtype)),
    timedtext: [...timedtext]
      .map((item) => ({
        language: item.language,
        element_subtype: item.element_subtype
      }))
      .sort((a, b) => a.language.localeCompare(b.language) || a.element_subtype.localeCompare(b.element_subtype))
  });

export const getFocusRightsPossible = createAsyncThunk(
  'focusNewSchedule/getFocusRightsPossible',
  async (payload: { title_id: number[]; license: string[]; availed_platforms: string[]; term_start__gte: string; term_end__lt: string }) => {
    globalAbortController.abort();
    globalAbortController = new AbortController();

    const response = await axios
      .post(`/focus/rights/possible/`, payload, { signal: globalAbortController.signal, baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsPossibleResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
      });

    return response ? { ...response, title_id: payload.title_id } : undefined;
  }
);

export const getFocusRightsValidate = createAsyncThunk(
  'focusNewSchedule/getFocusRightsValidate',
  async (payload: {
    title_id: number[];
    license: string[];
    availed_platform: string[];
    term_start__gte: string;
    term_end__lt: string;
    territories: string[];
    audio: FocusRightCheckObject[];
    timedtext: FocusRightCheckObject[];
  }) => {
    globalAbortController.abort();
    globalAbortController = new AbortController();

    const response = await axios
      .post(`/focus/rights/validate/`, payload, { signal: globalAbortController.signal, baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsValidateResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
      });

    return response
      ? {
          ...response,
          title_id: payload.title_id,
          term_start__gte: payload.term_start__gte,
          term_end__lte: payload.term_end__lte
        }
      : undefined;
  }
);

export const getFocusRightsPossibleBySchedulingId = createAsyncThunk(
  'focusNewSchedule/getFocusRightsPossibleBySchedulingId',
  async (payload: {
    schedulingId: number;
    title_id: number[];
    license: string[];
    availed_platforms: string[];
    term_start__gte: string;
    term_end__lt: string;
  }) => {
    globalAbortController.abort();
    globalAbortController = new AbortController();
    const realPayload = { ...payload, schedulingId: undefined };

    const response = await axios
      .post(`/focus/rights/possible/`, realPayload, { signal: globalAbortController.signal, baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsPossibleResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
      });

    return { response, schedulingId: payload.schedulingId };
  }
);

export const getFocusRightsValidateBySchedulingId = createAsyncThunk(
  'focusNewSchedule/getFocusRightsValidateBySchedulingId',
  async (payload: {
    schedulingId: number;
    title_id: number[];
    license: string[];
    availed_platform: string[];
    term_start__gte: string;
    term_end__lt: string;
    territories: string[];
    audio: FocusRightCheckObject[];
    timedtext: FocusRightCheckObject[];
  }) => {
    globalAbortController.abort();
    globalAbortController = new AbortController();
    const realPayload = { ...payload, schedulingId: undefined };

    const response = await axios
      .post(`/focus/rights/validate/`, realPayload, { signal: globalAbortController.signal, baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsValidateResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
      });

    return { response, schedulingId: payload.schedulingId };
  }
);

export const getFocusRightsValidateById = createAsyncThunk(
  'focusNewSchedule/getFocusRightsValidateById',
  async (payload: {
    title_id: number[];
    license: string[];
    availed_platform: string[];
    term_start__gte: string;
    term_end__lt: string;
    territories: string[];
    audio: FocusRightCheckObject[];
    timedtext: FocusRightCheckObject[];
  }) => {
    globalAbortController.abort();
    globalAbortController = new AbortController();

    const response = await axios
      .post(`/focus/rights/validate/`, payload, { signal: globalAbortController.signal, baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsValidateResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
      });

    return response
      ? {
          ...response,
          title_id: payload.title_id,
          term_start__gte: payload.term_start__gte,
          term_end__lte: payload.term_end__lte
        }
      : undefined;
  }
);

export const getFocusRightsValidateForLaunch = createAsyncThunk(
  'focusNewSchedule/getFocusRightsValidateForLaunch',
  async (
    payload: {
      launchId: string;
      versionIds: number[];
      license: string[];
      availed_platform: string[];
      term_start__gte: string;
      term_end__lt: string;
      territories: string[];
      audio: FocusRightCheckObject[];
      timedtext: FocusRightCheckObject[];
    },
    { rejectWithValue }
  ) => {
    if (!payload.versionIds.length) {
      return rejectWithValue({
        launchId: payload.launchId,
        versionIds: payload.versionIds,
        reason: 'no_versions'
      });
    }

    const requestPayload = {
      title_id: payload.versionIds,
      license: payload.license,
      availed_platform: payload.availed_platform,
      term_start__gte: payload.term_start__gte,
      term_end__lte: payload.term_end__lte,
      territories: payload.territories.map((territory) => territory.toLowerCase()),
      audio: payload.audio.map((item) => ({
        language: item.language,
        element_subtype: item.element_subtype
      })),
      timedtext: payload.timedtext.map((item) => ({
        language: item.language,
        element_subtype: item.element_subtype
      }))
    };

    const response = await axios
      .post(`/focus/rights/validate/`, requestPayload, { baseURL: '/api/v1.1' })
      .then((res: AxiosResponse<FocusRightsValidateResponse>) => res.data.data)
      .catch((err) => {
        console.error(err);
        return null;
      });

    if (!response) {
      return rejectWithValue({
        launchId: payload.launchId,
        versionIds: payload.versionIds,
        reason: 'request_failed'
      });
    }

    return {
      launchId: payload.launchId,
      versionIds: payload.versionIds,
      response
    };
  }
);

/**
 * Revalidates all incremental launches.
 * Validation is batched per launch, grouping versions by identical resolved rights payload.
 */
export const validateAllIncrementalLaunches = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const focus = state.focusNewSchedule;

  if (!focus?.startDate) return;

  const {
    launchList,
    selectedLicense,
    selectedPlatforms,
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedVersions,
    validationWindowByVersionId
  } = focus;

  const incrementalLaunches = launchList.filter((launch) => launch.type === 'incremental');

  for (const launch of incrementalLaunches) {
    if (!selectedVersions.length) {
      continue;
    }

    const groupedBatches = new Map<string, LaunchValidationBatch>();

    selectedVersions.forEach((versionId) => {
      const validationWindow = validationWindowByVersionId[versionId];

      if (!validationWindow?.term_start__gte || !validationWindow?.term_end__lte) {
        return;
      }

      const expansionState = getLaunchValidationExpansionState({
        launch,
        launchList,
        versionId,
        originalSelectedTerritories: selectedTerritories,
        originalSelectedDubs: selectedDubs,
        originalSelectedSubs: selectedSubs,
        originalSelectedVersions: selectedVersions
      });

      const batchWithoutVersionIds = {
        term_start__gte: validationWindow.term_start__gte,
        term_end__lte: validationWindow.term_end__lte,
        territories: expansionState.resolvedTerritories,
        audio: expansionState.resolvedDubs.map((language) => ({
          language,
          element_subtype: 'full'
        })),
        timedtext: expansionState.resolvedSubs.map((language) => ({
          language,
          element_subtype: 'full-mix'
        }))
      };

      const batchKey = createLaunchValidationBatchKey(batchWithoutVersionIds);
      const existingBatch = groupedBatches.get(batchKey);

      if (existingBatch) {
        existingBatch.versionIds.push(versionId);
        return;
      }

      groupedBatches.set(batchKey, {
        versionIds: [versionId],
        ...batchWithoutVersionIds
      });
    });

    for (const batch of groupedBatches.values()) {
      await dispatch(
        getFocusRightsValidateForLaunch({
          launchId: launch.id,
          versionIds: batch.versionIds,
          license: [selectedLicense],
          availed_platform: selectedPlatforms,
          term_start__gte: batch.term_start__gte,
          term_end__lte: batch.term_end__lte,
          territories: batch.territories,
          audio: batch.audio,
          timedtext: batch.timedtext
        })
      );
    }
  }
};

export const addLaunchAndValidateRights =
  (payload: { launch: Launch }): AppThunk =>
  (dispatch) => {
    dispatch(addLaunch(payload));
    dispatch(validateAllIncrementalLaunches());
  };

export const updateLaunchAndValidateRights =
  (payload: { launchId: string; patch: Partial<Launch> }): AppThunk =>
  (dispatch) => {
    dispatch(updateLaunch(payload));
    dispatch(validateAllIncrementalLaunches());
  };

export const deleteLaunchAndValidateRights =
  (payload: { launchId: string }): AppThunk =>
  (dispatch) => {
    dispatch(deleteLaunch(payload));
    dispatch(validateAllIncrementalLaunches());
  };

export const ensureScheduleGroupsInitialized = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const focus: FocusNewScheduleState | undefined = state?.focusNewSchedule;

  if (!focus) return;

  const versionsUnsorted = selectVersions(state) ?? [];
  const versions = [...versionsUnsorted].sort((a, b) => {
    const orderA = a.lineage_meta?.episode_sequence_order || a.lineage_meta?.ova_sequence_order || a.sequence_order || 0;
    const orderB = b.lineage_meta?.episode_sequence_order || b.lineage_meta?.ova_sequence_order || b.sequence_order || 0;
    return orderA - orderB;
  });

  const versionIds = versions.map((v) => v.id);
  if (!focus.startDate || versionIds.length === 0) return;

  const platformsKey = [...(focus.selectedPlatforms ?? [])].sort().join(',');
  const territoriesKey = [...(focus.selectedTerritories ?? [])].sort().join(',');
  const dubsKey = [...(focus.selectedDubs ?? [])].sort().join(',');
  const subsKey = [...(focus.selectedSubs ?? [])].sort().join(',');

  const contextKey = [
    versionIds.join(','),
    focus.frequency,
    focus.startDate,
    focus.endDate ?? '',
    focus.selectedLicense ?? '',
    platformsKey,
    territoriesKey,
    dubsKey,
    subsKey
  ].join('|');

  const createDefaultGroup = (versionId: number, index: number): ScheduleGroup => [
    {
      versionId,
      occurrenceStartDate: getOccurrenceIso(focus.startDate, focus.frequency, index),
      selectedTerritories: focus.selectedTerritories,
      selectedDubs: focus.selectedDubs,
      selectedSubs: focus.selectedSubs,
      selectedVersions: focus.selectedVersions
    }
  ];

  const prevById = new Map<number, ScheduleGroup>();
  focus.scheduleGroups.forEach((g) => {
    const id = g[0]?.versionId;
    if (id != null) prevById.set(id, g);
  });

  const isNewContext = focus.scheduleContextKey !== contextKey;

  const nextGroups = isNewContext
    ? versionIds.map((id, i) => createDefaultGroup(id, i))
    : versionIds.map((id, i) => prevById.get(id) ?? createDefaultGroup(id, i));

  dispatch(
    applyInitializedScheduleGroups({
      contextKey,
      scheduleGroups: nextGroups,
      resetReviewChanges: isNewContext
    })
  );
};

const FocusNewScheduleSlice = createSlice({
  name: 'focusNewSchedule',
  initialState,
  reducers: {
    clearNewSchedule: (state) => {
      Object.assign(state, initialState);
    },
    setScheduleData: (state, { payload }: { payload: Partial<FocusNewScheduleState> }) => {
      Object.assign(state, payload);
    },
    applyInitializedScheduleGroups: (
      state,
      {
        payload
      }: {
        payload: { contextKey: string; scheduleGroups: ScheduleGroup[]; resetReviewChanges?: boolean };
      }
    ) => {
      state.scheduleContextKey = payload.contextKey;
      state.scheduleGroups = payload.scheduleGroups;
      if (payload.resetReviewChanges) state.hasReviewChanges = false;
    },
    setRightsTask: (state, { payload }: { payload: { status: 'none' | 'pending' | 'completed'; id: number | null } }) => {
      state.rightsTask = payload;
    },
    setRightsTaskBySchedulingId: (
      state,
      { payload }: { payload: { schedulingId: number; status: 'none' | 'pending' | 'completed'; id: number | null } }
    ) => {
      state.rightsTaskBySchedulingId[payload.schedulingId] = { status: payload.status, id: payload.id };
    },
    setScheduleGroups: (state, { payload }: { payload: ScheduleGroup[] }) => {
      state.scheduleGroups = payload;
    },
    initializeLaunchList: (state) => {
      if (state.launchList.length) {
        return;
      }

      state.launchList = [createLaunch('first-time')];
    },
    addLaunch: (
      state,
      action: PayloadAction<{
        launch: Launch;
      }>
    ) => {
      state.launchList = [...state.launchList, action.payload.launch];
    },
    updateLaunch: (
      state,
      action: PayloadAction<{
        launchId: string;
        patch: Partial<Launch>;
      }>
    ) => {
      const { launchId, patch } = action.payload;

      const updatedLaunchList = state.launchList.map((launch) =>
        launch.id === launchId
          ? {
              ...launch,
              ...patch
            }
          : launch
      );

      const changedIndex = updatedLaunchList.findIndex((launch) => launch.id === launchId);

      state.launchList =
        changedIndex === -1
          ? updatedLaunchList
          : rebaseFollowingLaunches({
              launchList: updatedLaunchList,
              startIndex: changedIndex + 1,
              selectedTerritories: state.selectedTerritories,
              selectedDubs: state.selectedDubs,
              selectedSubs: state.selectedSubs,
              selectedVersions: state.selectedVersions
            });

      Object.values(state.rightsValidateByVersionId).forEach((versionState) => {
        delete versionState.launchesById[launchId];
      });
    },
    deleteLaunch: (
      state,
      action: PayloadAction<{
        launchId: string;
      }>
    ) => {
      const deletedIndex = state.launchList.findIndex((launch) => launch.id === action.payload.launchId);

      if (deletedIndex === -1) {
        return;
      }

      //Kyle Update for the new flow where all new increments are really edits of the first
      const deletedLaunch = state.launchList[deletedIndex];

      const selectedTerritories = state.selectedTerritories.concat(deletedLaunch.incrementalLaunchRightsDraftByTab.territories.add ?? []);
      const selectedDubs = state.selectedDubs.concat(deletedLaunch.incrementalLaunchRightsDraftByTab.audio.add ?? []);
      const selectedSubs = state.selectedSubs.concat(deletedLaunch.incrementalLaunchRightsDraftByTab.timedtext.add ?? []);
      // end update
      const nextLaunchList = state.launchList.filter((launch) => launch.id !== action.payload.launchId);

      state.launchList = rebaseFollowingLaunches({
        launchList: nextLaunchList,
        startIndex: deletedIndex,
        selectedTerritories: selectedTerritories,
        selectedDubs: selectedDubs,
        selectedSubs: selectedSubs,
        selectedVersions: state.selectedVersions
      });

      //Kyle Update for the new flow where all new increments are really edits of the first
      state.selectedTerritories = selectedTerritories;
      state.selectedDubs = selectedDubs;
      state.selectedSubs = selectedSubs;
      // end update
      Object.values(state.rightsValidateByVersionId).forEach((versionState) => {
        delete versionState.launchesById[action.payload.launchId];
      });
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getFocusRightsPossible.fulfilled, (state, { payload }) => {
      if (!payload?.territories) return;

      const rightsPossibleData: FocusRightsPossibleResponse['data'] = {
        territories: payload.territories
      };

      state.rightsPossible = rightsPossibleData;

      if (payload.title_id?.length === 1) {
        const [id] = payload.title_id;
        state.rightsPossibleById[id] = rightsPossibleData;
      }
    });

    builder.addCase(getFocusRightsValidate.fulfilled, (state, { payload }) => {
      if (!payload?.title_id?.length) {
        return;
      }

      state.rightsValidate = payload.is_valid ? null : payload.invalid_rights;

      const invalidRightsByTitleId = getInvalidRightsByTitleId(payload.invalid_rights);

      payload.title_id.forEach((id: number) => {
        const invalidRightsForVersion = invalidRightsByTitleId[id] ?? [];

        setBaseValidationForVersion({
          state,
          versionId: id,
          validationResult: payload.is_valid ? 'valid' : invalidRightsForVersion,
          termStart: payload.term_start__gte,
          termEnd: payload.term_end__lte
        });
      });
    });

    builder.addCase(getFocusRightsValidateById.fulfilled, (state, { payload }) => {
      if (!payload?.title_id?.length) {
        return;
      }

      const invalidRightsByTitleId = getInvalidRightsByTitleId(payload.invalid_rights);

      payload.title_id.forEach((id: number) => {
        const invalidRightsForVersion = invalidRightsByTitleId[id] ?? [];
        const validationResult: RightsValidationResult = payload.is_valid ? 'valid' : invalidRightsForVersion;

        state.rightsValidateById[id] = validationResult;

        setBaseValidationForVersion({
          state,
          versionId: id,
          validationResult,
          termStart: payload.term_start__gte,
          termEnd: payload.term_end__lte
        });
      });
    });

    builder.addCase(getFocusRightsPossibleBySchedulingId.fulfilled, (state, { payload }) => {
      if (!payload?.response?.territories) return;

      state.rightsPossibleBySchedulingId[payload.schedulingId] = {
        territories: payload.response.territories
      };
    });

    builder.addCase(getFocusRightsValidateBySchedulingId.fulfilled, (state, { payload }) => {
      if (payload?.response?.is_valid) {
        state.rightsValidateBySchedulingId[payload.schedulingId] = 'valid';
      } else if (payload?.response?.invalid_rights) {
        state.rightsValidateBySchedulingId[payload.schedulingId] = payload.response.invalid_rights;
      }
    });

    builder.addCase(getFocusRightsValidateForLaunch.fulfilled, (state, { payload }) => {
      setLaunchValidationForVersions({
        state,
        versionIds: payload.versionIds,
        launchId: payload.launchId,
        isValid: payload.response.is_valid,
        invalidRights: payload.response.invalid_rights
      });
    });

    builder.addCase(getFocusRightsValidateForLaunch.rejected, (state, action) => {
      const payload = action.payload as { versionIds?: number[]; launchId?: string } | undefined;

      if (!payload?.versionIds?.length || !payload?.launchId) return;

      payload.versionIds.forEach((versionId) => {
        ensureVersionLaunchValidationState(state, versionId);
        delete state.rightsValidateByVersionId[versionId].launchesById[payload.launchId as string];
      });
    });
  }
});

export default FocusNewScheduleSlice.reducer;

export const {
  clearNewSchedule,
  setScheduleData,
  applyInitializedScheduleGroups,
  setRightsTask,
  setScheduleGroups,
  setRightsTaskBySchedulingId,
  initializeLaunchList,
  addLaunch,
  updateLaunch,
  deleteLaunch
} = FocusNewScheduleSlice.actions;
